import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation  } from "react-router-dom";
import { Form, Input, Button, notification } from "antd";
import { UserOutlined, LockOutlined, SafetyOutlined } from "@ant-design/icons";
import { signInApi, verifyTwoFactorApi, setSession } from "../../api/user";
import OtpCodeInput from "../OtpCodeInput/OtpCodeInput";
import { getAccessToken } from "../../api/token";
import { withLocale } from "../../l10n";
import "./LoginForm.scss";

function LoginForm(props) {
  const navigate = useNavigate();
  const location = useLocation();

  // --- Estado de la primera fase (login) ---
  const [inputs, setInputs] = useState({
    username: '',
    password: ''
  });

  // --- Estado de la segunda fase (2FA) ---
  const [twoFaRequired, setTwoFaRequired] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  useEffect(() => {
    if (location.state?.errorNotification) {
       notification.error({
        message: location.state.errorNotification,
        duration: 0
      });
    }
  }, [location]);

  const onChange = loginValues => {
    setInputs({
      ...inputs,
      [loginValues.target.name]: loginValues.target.value,
    });
  };

  const persistSessionAndGo = (result) => {
    setSession(result);
    notification["success"]({
      message: props.lang.handle('Inicio de sesión exitoso'),
    });
    navigate(0);
  };

  const onLogin = async () => {
    const { username, password } = inputs;

    if (!password || !username) {
      notification["error"]({
        message: props.lang.handle('Complete los campos'),
      });
      return;
    }

    const result = await signInApi(inputs);

    // Si la cuenta está bloqueada temporalmente, mostramos un error y no seguimos.
    if (result.twoFactorLocked) {
      notification["error"]({
        message: props.lang.handle('Cuenta bloqueada temporalmente por demasiados intentos. Inténtelo más tarde.'),
      });
      return;
    }

    // Si el usuario tiene 2FA, pasamos a pedir el código.
    if (result.twoFactorRequired) {
      setPendingToken(result.pendingToken);
      setTwoFaRequired(true);
      return;
    }

    if (!result.ok) {
      notification["error"]({
        message: props.lang.handle(result.message),
      });
      return;
    }

    persistSessionAndGo(result);
  };

  const submitCode = async (value) => {
    if (!value) {
      notification["error"]({
        message: props.lang.handle('Introduzca el código'),
      });
      return;
    }

    const result = await verifyTwoFactorApi({
      pendingToken,
      code: value,
      type: useRecovery ? 'recovery' : 'totp',
    });

    if (result.twoFactorLocked || result.twoFactorExpired) {
      cancelTwoFa();
      notification["error"]({
        message: props.lang.handle(
          result.twoFactorLocked
            ? 'Cuenta bloqueada temporalmente por demasiados intentos. Inténtelo más tarde.'
            : 'La verificación ha caducado. Inicie sesión de nuevo.'
        ),
      });
      return;
    }

    if (!result.ok) {
      setCode('');
      notification["error"]({
        message: props.lang.handle(result.message),
      });
      return;
    }

    persistSessionAndGo(result);
  };

  const cancelTwoFa = () => {
      setTwoFaRequired(false);
      setPendingToken(null);
      setCode('');
      setUseRecovery(false);
    };

  if (getAccessToken()) {
    return (navigate(0));
  }

  // --- Segunda fase: introducir el código de verificación ---
 if (twoFaRequired) {
    return (
      <Form className="login-form" onFinish={() => submitCode(code)}>
        <Form.Item>
          <p style={{ margin: 0 }}>
            {useRecovery
              ? props.lang.handle('Introduzca uno de sus códigos de recuperación')
              : props.lang.handle('Introduzca el código de su aplicación de autenticación')}
          </p>
        </Form.Item>

        <Form.Item>
          {useRecovery ? (
            <Input
              prefix={<SafetyOutlined className="site-form-item-icon" />}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={props.lang.handle('Código de recuperación')}
              autoFocus
            />
          ) : (
            <OtpCodeInput
              value={code}
              onChange={setCode}
              onComplete={submitCode}
            />
          )}
        </Form.Item>

        {useRecovery && (
          <Form.Item>
            <Button type="primary" htmlType="submit" className="login-form-button">
              {props.lang.handle('Verificar')}
            </Button>
          </Form.Item>
        )}

        <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
          <Button
            type="link"
            onClick={() => { setUseRecovery(!useRecovery); setCode(''); }}
          >
            {useRecovery
              ? props.lang.handle('Usar el código de la aplicación')
              : props.lang.handle('Usar un código de recuperación')}
          </Button>
          <Button type="link" onClick={cancelTwoFa}>
            {props.lang.handle('Volver')}
          </Button>
        </Form.Item>
      </Form>
    );
  }
  // --- Primera fase: usuario y contraseña ---
  return (
    <Form
      className="login-form"
      onChange={onChange}
      onFinish={onLogin}
    >
      <Form.Item
        rules={[
          {
            required: true,
            message: props.lang.handle('Introduzca un nombre de usuario'),
          },
        ]}
      >
        <Input
          prefix={<UserOutlined className="site-form-item-icon" />}
          name="username"
          type="username"
          placeholder={props.lang.handle("Nombre de usuario") + "."} 
        />
      </Form.Item>
      <Form.Item
        rules={[
          {
            required: true,
            message: props.lang.handle('Introduzca una contraseña') + ".",
          },
        ]}
      >
        <Input.Password
          prefix={<LockOutlined className="site-form-item-icon" />}
          name="password"
          placeholder={props.lang.handle('Contraseña')}
        />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" className="login-form-button">
          {props.lang.handle('Iniciar sesión')}
        </Button>
      </Form.Item>
      <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
        <Link to="/forgot-password">
          {props.lang.handle('¿Olvidó su contraseña?')}
        </Link>
      </Form.Item>
    </Form>
  );
};

export default withLocale(LoginForm);
